import Draggable from "react-draggable";
import { useReactDraggable } from "../../../../hooks/useReactDraggable";
import { Button, Modal, Table, Descriptions, Divider, Row, Col } from "antd";
import { FormattedAdvanceBookingData } from "../../../../shared/interface/advanceBookingRequest";
import { useEffect, useState } from "react";
import * as advanceBookingRequestService from "../../../../services/advanceBookingRequestService";
import { CustomNotification } from "../../../../util/CustomNotification";
import moment from "moment-timezone";
import useValue from "../../../../hooks/useValue";
import Loading from "../../../common/Loading";

interface AdvanceBookingDetailsModalProps {
  handleOnClose: () => void;
  isModalVisible: boolean;
  abrRecordId: number | null;
}

const AdvanceBookingDetailsModal = (props: AdvanceBookingDetailsModalProps) => {
  /* HOOK */
  const { bounds, onStart } = useReactDraggable();
  const { value: loading, handleEnableValue: handleEnableLoading, handleDisableValue: handleDisableLoading } = useValue(false);

  /* STATE */
  const [abrDetails, setAbrDetails] = useState<FormattedAdvanceBookingData | null>(null);

  /* EFFECT */
  useEffect(() => {
    const fetchAbrDetails = async (abrId: number) => {
      try {
        if (!abrId) return;
        handleEnableLoading();
        const response = await advanceBookingRequestService.getAbrDetailsAPI(abrId);
        if (response) {
          const formattedResponse: any = { ...response.data };

          formattedResponse["parking_amount"] = response.data.amount.parking_amount;
          formattedResponse["valet_parking_amount"] = response.data.amount.valet_parking_amount;
          formattedResponse["penalty_amount"] = response.data.amount.penalty_amount;
          formattedResponse["overdue_amount"] = response.data.amount.overdue_amount;
          formattedResponse["payable_amount"] = response.data.amount.payable_amount;

          setAbrDetails(formattedResponse as FormattedAdvanceBookingData);
        }
      } catch (error) {
        CustomNotification.showErrorMessage(error);
      } finally {
        handleDisableLoading();
      }
    };
    if (props.abrRecordId) fetchAbrDetails(props.abrRecordId);
  }, [props.abrRecordId]);

  return (
    <Modal
      title={<div className="modal-container">Advance Booking Details</div>}
      open={props.isModalVisible}
      onCancel={props.handleOnClose}
      cancelText="Close"
      width="200vh"
      destroyOnClose={true}
      maskClosable={false}
      wrapClassName="delete-modal"
      modalRender={(modal) => (
        <Draggable bounds={bounds} handle=".ant-modal-header" onStart={(event, uiData) => onStart(event as any, uiData)}>
          {modal}
        </Draggable>
      )}
      footer={[
        <Button key="close" onClick={props.handleOnClose}>
          Close
        </Button>
      ]}
    >
      <>
        {loading && <Loading />}
        {!loading && (
          <>
            <Row gutter={16}>
              {/* LEFT SECTION */}
              <Col span={12}>
                <Descriptions column={1} size="small" bordered title="Booking Info">
                  <Descriptions.Item label="Booking ID">{abrDetails?.booking_id}</Descriptions.Item>
                  <Descriptions.Item label="Booking In Time">{abrDetails?.parking_start_date ? moment(abrDetails.parking_start_date).format("HH:mm") : ""}</Descriptions.Item>
                  <Descriptions.Item label="Booking Out Time">{abrDetails?.parking_end_date ? moment(abrDetails.parking_end_date).format("HH:mm") : ""}</Descriptions.Item>
                  <Descriptions.Item label="Parking In Time">{abrDetails?.parking_in_time ? moment(abrDetails.parking_in_time, "DD-MM-YYYY HH:mm:ss").format("HH:mm") : ""}</Descriptions.Item>
                  <Descriptions.Item label="Parking Out Time">{abrDetails?.parking_out_time ? moment(abrDetails.parking_out_time, "DD-MM-YYYY HH:mm:ss").format("HH:mm") : ""}</Descriptions.Item>
                  <Descriptions.Item label="Vehicle Type">{abrDetails?.vehicleType?.vehicle_type_name}</Descriptions.Item>
                  <Descriptions.Item label="Vehicle Number">{abrDetails?.keyFobAllocation?.vehicle_number}</Descriptions.Item>
                  <Descriptions.Item label="Vehicle Dimensions">{abrDetails?.vehicleType?.dimension}</Descriptions.Item>
                  <Descriptions.Item label="Key Fob Token">{abrDetails?.keyFobAllocation?.keyFob?.key_fob_token}</Descriptions.Item>
                  <Descriptions.Item label="Mobile User">
                    {abrDetails?.keyFobAllocation?.mobileUser?.first_name} {abrDetails?.keyFobAllocation?.mobileUser?.last_name}
                  </Descriptions.Item>
                  <Descriptions.Item label="Mobile Number">{abrDetails?.keyFobAllocation?.mobileUser?.mobile_number}</Descriptions.Item>
                </Descriptions>
              </Col>

              {/* RIGHT SECTION */}
              <Col span={12}>
                <Descriptions column={1} size="small" bordered title={<>&nbsp;</>}>
                  <Descriptions.Item label="Location Name">{abrDetails?.customerLocation?.location_name}</Descriptions.Item>
                  <Descriptions.Item label="Location Address">{abrDetails?.customerLocation?.location_address}</Descriptions.Item>

                  <Descriptions.Item label="Is Penalty Applied?">{abrDetails?.is_penalty ? "Yes" : "No"}</Descriptions.Item>
                  <Descriptions.Item label="Is Valet Parking?">{abrDetails?.is_valet_parking ? "Yes" : "No"}</Descriptions.Item>
                  <Descriptions.Item label="Payment Stage">{abrDetails?.payment_stage}</Descriptions.Item>
                  <Descriptions.Item label="Valet Amount">{abrDetails?.valet_parking_amount} ₹</Descriptions.Item>
                  <Descriptions.Item label="Overdue Amount">{abrDetails?.overdue_amount} ₹</Descriptions.Item>
                  <Descriptions.Item label="Penalty Amount">{abrDetails?.penalty_amount} ₹</Descriptions.Item>
                  <Descriptions.Item label="Parking Amount">{abrDetails?.parking_amount} ₹</Descriptions.Item>
                  <Descriptions.Item label="Payable Amount">{abrDetails?.payable_amount} ₹</Descriptions.Item>
                  <Descriptions.Item label="Created On">{moment(abrDetails?.created_on).format("DD-MM-YYYY HH:mm")}</Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>

            <Divider orientation="left">Payment Log</Divider>
            <Table
              size="small"
              columns={[
                { title: "Transaction Date", dataIndex: "transaction_date", key: "transaction_date", render: (text) => <div>{text ? moment(text).format("DD-MM-YYYY HH:mm") : ""}</div> },
                { title: "Amount", dataIndex: "total_amount", key: "total_amount", render: (text) => <div>{text} ₹</div> },
                {
                  title: "Mode",
                  dataIndex: "payment_mode",
                  render: (text, record) => (
                    <div>
                      {record.payment_mode} {record.payment_gateway && `/ ${record.payment_gateway ?? ""}`}
                    </div>
                  )
                },
                { title: "Status", dataIndex: "payment_status", key: "payment_status" }
              ]}
              dataSource={abrDetails?.paymentLog || []}
              rowKey="id"
              pagination={false}
            />

            <Divider orientation="left">Parking History</Divider>
            <Table
              size="small"
              columns={[
                { title: "Request Type", dataIndex: "requestType", key: "requestType" },
                { title: "Status", dataIndex: "requestStatus", key: "requestStatus" },
                { title: "Date", dataIndex: "created_on", key: "created_on", render: (text) => <div>{text ? moment(text).format("DD-MM-YYYY HH:mm") : ""}</div> }
              ]}
              dataSource={abrDetails?.parkingHistory || []}
              rowKey="id"
              pagination={false}
            />
          </>
        )}
      </>
    </Modal>
  );
};

export default AdvanceBookingDetailsModal;
